import HasID from "./HasID";
import TrackedObject from "./TrackedObject";
import { Person } from "./Person";
import moment from "moment";
import { PhoneNumber } from "@/models/PhoneNumber";
import { OptStatus, PhoneOptStatus } from "@/models/OptStatus";
import { SpeciesCode } from "@/models/SpeciesCode";
import HasSearchHelp from "./HasSearchHelp";
import PhoneFormatter from "@/util/PhoneFormatter";

export interface Patient extends Person {
    id: number;

    dateOfBirth: Date;
    sex: string;

    emailAddress: string;
    phoneNumber: string;
    cellNumber: string;
    maritalStatus: string;
    height: number; // inches
    weight: number; // lbs
    residenceCode: string;
    pregnant?: Date;
    lactating?: Date;
    smoker?: Date;
    medSyncOptOutDate?: Date;
    deceased?: Date;
    isHighTouch: boolean;
    licenseState: string;
    licenseNumber: string;
    licenseExpirationDate?: Date;
    headOfHousehold?: number;
    primaryLanguage?: number; // some kind of code?? Backend has this as an int.
    alert: string; // Note to popup for patient anytime it is viewed
    childCaps: boolean;
    signatureRequired: boolean;
    nkda: boolean;
    defaultDelivery: number; // some kind of code? probably want to make an enum
    carriers: string[];
    hipaaDocsReceived: Date;
    species: string;
    speciesCode: SpeciesCode;
    programs: Array<PatientProgram>;
    stripeCustomerId: string;
    ssn: string;
    cellId: number;
    phoneId: number;
    phone: PhoneNumber;
    cell: PhoneNumber;
    phoneOptStatus: OptStatus;
    cellOptStatus: OptStatus;
    patientAppUserName: string;
    primaryPrescriberId?: number;
    preferredName?: string;
    sexAssignedAtBirth?: string;
    gender?: string;
    pronouns?: string;
    textMessageOptOutDate?: Date;
    textMessageOptOutReason?: string;
    addresses: Array<PatientAddress>;
    programEligibilities: PatientProgramEligibility[];
}

export enum Language {
    English,
    Spanish,
    Russian,
    Somali,
    Arabic,
    ChineseSimplified,
    Vietnamese,
    Farsi,
    Korean,
    Romanian,
    Swahili,
    Burmese,
    Nepali,
    Amharic,
    Pashto,
}

export enum PatientAddressType {
    mailing,
    shipping,
    billing
}

export class Patient extends Person implements HasSearchHelp {
    constructor(obj?: Patient) {
        super();
        //this.id = 0;
        this.childCaps = true;
        this.signatureRequired = false;
        this.residenceCode = "0";
        this.primaryLanguage = 0;
        this.speciesCode = SpeciesCode.Human;
        this.isHighTouch = false;

        if (obj) {
            Object.assign(this, obj);

        }

        if (obj?.programEligibilities?.length) {
            this.programEligibilities = obj.programEligibilities;
        } else {
            this.programEligibilities = [];
        }

        if (!this.addresses?.length) {
            this.addresses = [{ id: -1, type: PatientAddressType.mailing, isPrimary: true }];
        }
    }

    getSearchHelpHtml(): string {
        return "<p>Search for patients using any of the following formats:</p>" +
            "<ul>" +
            "<li>Phone Number: \"+1XXX...\"</li>" +
            "<li>Patient Name: \"LastName, FirstName\"</li>" +
            "<li>Date of Birth: \"6/17/2010\"</li>" +
            "<li>Partner Fill Number: \"P-1426732\"</li>" +
            "<li>Program Identifier: \"PP-123XYZ\"</li>" +
            "<li>DOB + Name: \"1/1/1970 Jacobson\"</li>" +
            "<li>Email: \"john@domain.com\"</li>" +
            "</ul>";
    }

    toString(): string {
        let label = "";
        if (this.id && this.lastName) {
            const dobString =
                this && this.dateOfBirth
                    ? new Date(this.dateOfBirth).toLocaleDateString()
                    : "No DOB";
            label =
                "<b>" +
                this.displayNamesForPerson() +
                "</b> - " +
                dobString +
                " - " +
                this.address1 +
                `, ${this.addressCity ?? ""}, ${this.addressState ?? ""}` +
                (this.id > 0 ? " <small>(ID: " +
                    this.id + ")</small>" : "")
            ;
        }
        if (label == '') {
            label = '<div class="font-weight-light">DOB/Name/Phone/Email</div>';
        }
        return label;
    }

    get age(): number | null {
        if (this.dateOfBirth) return moment().diff(this.dateOfBirth, "years");
        else return null;
    }

    get languageName(): string {
        if (this.primaryLanguage === undefined || this.primaryLanguage === null)
            return "";

        return Language[this.primaryLanguage];
    }

    get phoneDisplay(): string {
        if (this.phone?.nationalFormat) {
            return this.phone.nationalFormat;
        }
        const rawPhone = this.phoneNumber ?? this.phone?.originalInput;
        return PhoneFormatter.formatForDisplay(rawPhone);
    }

    get cellDisplay(): string {
        // Prefer nationalFormat from validated phone, otherwise format the raw number
        if (this.cell?.nationalFormat) {
            return this.cell.nationalFormat;
        }
        const rawCell = this.cellNumber ?? this.cell?.originalInput;
        return PhoneFormatter.formatForDisplay(rawCell);
    }

    /**
     * Returns the phone number in Twilio-compatible E.164 format (+15551234567).
     * Use this for copy-paste functionality.
     */
    get phoneTwilio(): string {
        // Prefer internationalFormat from validated phone
        if (this.phone?.internationalFormat) {
            return this.phone.internationalFormat;
        }
        const rawPhone = this.phoneNumber ?? this.phone?.originalInput;
        return PhoneFormatter.formatForTwilio(rawPhone);
    }

    /**
     * Returns the cell number in Twilio-compatible E.164 format (+15551234567).
     * Use this for copy-paste functionality.
     */
    get cellTwilio(): string {
        // Prefer internationalFormat from validated phone
        if (this.cell?.internationalFormat) {
            return this.cell.internationalFormat;
        }
        const rawCell = this.cellNumber ?? this.cell?.originalInput;
        return PhoneFormatter.formatForTwilio(rawCell);
    }

    get optInOptOutPhoneStatus(): PhoneOptStatus {
        return this.optStatus(this.phoneOptStatus?.optIn ?? null, this.phoneOptStatus?.optOut ?? null);
    }

    get optInOptOutCellPhoneStatus(): PhoneOptStatus {
        return this.optStatus(this.cellOptStatus?.optIn ?? null, this.cellOptStatus?.optOut ?? null);
    }

    private optStatus(optIn: Date | null, optOut: Date | null): PhoneOptStatus {
        if (this.textMessageOptOutDate) return PhoneOptStatus.optOut;

        if (optIn != null) optIn = moment(optIn).toDate();
        if (optOut != null) optOut = moment(optOut).toDate();

        if (optIn != null && optOut != null) return optOut > optIn ? PhoneOptStatus.optOut : PhoneOptStatus.optIn;
        if (optIn != null) return PhoneOptStatus.optIn;
        if (optOut != null) return PhoneOptStatus.optOut;
        return PhoneOptStatus.unknown;
    }

    get phoneOptDateInfo(): string {
        if (this.textMessageOptOutDate)
            return `Text History<br>Opt Out: ${this.formatOptDate(this.textMessageOptOutDate)}`;

        const status = this.optInOptOutPhoneStatus;
        if (status === PhoneOptStatus.optOut)
            return `Text History<br>Opt Out: ${this.formatOptDate(this.phoneOptStatus?.optOut)}`;
        if (status === PhoneOptStatus.optIn)
            return `Text History<br>Opt In: ${this.formatOptDate(this.phoneOptStatus?.optIn)}`;
        return "Text History";
    }

    get cellOptDateInfo(): string {
        if (this.textMessageOptOutDate)
            return `Text History<br>Opt Out: ${this.formatOptDate(this.textMessageOptOutDate)}`;

        const status = this.optInOptOutCellPhoneStatus;
        if (status === PhoneOptStatus.optOut)
            return `Text History<br>Opt Out: ${this.formatOptDate(this.cellOptStatus?.optOut)}`;
        if (status === PhoneOptStatus.optIn)
            return `Text History<br>Opt In: ${this.formatOptDate(this.cellOptStatus?.optIn)}`;
        return "Text History";
    }

    private formatOptDate(value: Date | null): string {
        if (!value) return "";
        const momentDate = moment(value);
        return `${momentDate.format("LL")} (${momentDate.fromNow()})`;
    }
}

export enum ResidenceCode {
    NotSpecified,
    PatientsHome,
    SkilledNursing,
    Nursing,
    AssistedLiving,
    CustodialCare,
    GroupHome,
    InpatientPsychiatric,
    Psychiatric,
    IntermediateCare,
    SubstanceAbuseTreatment,
    Hospice,
    PsychiatricTreatment,
    ComprehensiveRehab,
    HomelessShelter,
    CorrectionalInstitution,
}

export interface PatientProgram extends HasID, TrackedObject {
    programID: number;
    patientID: number;
    programName: string;
    identifier: string;
}

export interface PatientAddress {
    id: number;
    patientId?: number;
    isPrimary: boolean;
    type: PatientAddressType;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    active?: boolean;
}

export interface PatientProgramEligibility {
    id: number;
    patientId: number;
    programId: number;
    eligible: boolean;
    reason?: string | null;
    approvalExpirationDate?: Date | null;
    created?: Date;
    createdBy?: string;
    updated?: Date;
    updatedBy?: string;
}
